import React from 'react'
// @ts-ignore
import { useDisplayTheme } from 'hooks/useUiState'
import {
  ExpandedState,
  SortingState,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getExpandedRowModel,
  ColumnDef,
  flexRender,
  RowData,
  CellContext
} from '@tanstack/react-table'
import {
  ArrowUp as ArrowUpIcon,
  ArrowDown as ArrowDownIcon,
  Table as NbUiTable
  // @ts-ignore
} from '@netbasequid/core'
import styled from 'styled-components'

declare module '@tanstack/react-table' {
  type NbCellProps = {
    fitWidth?: boolean
    style?: React.CSSProperties
    contentStyle?: React.CSSProperties
  }
  interface ColumnMeta<TData extends RowData, TValue> {
    cellProps?: NbCellProps
    cells?: ((context: CellContext<TData, TValue>) => React.ReactNode)[]
    cellsProps?: NbCellProps[]
  }
}

const HeaderInnerContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`

const ExpandedTable = <T,>({
  data,
  columns,
  getSubRows,
  initialState,
  onRowClick
}: {
  data: T[]
  columns: ColumnDef<T>[]
  getSubRows?: (row: T) => T[]
  initialState?: {
    expanded?: ExpandedState
    sorting?: SortingState
  }
  onRowClick?: (row: T) => void
}) => {
  const mode = useDisplayTheme()
  const [expanded, setExpanded] = React.useState<ExpandedState>(initialState?.expanded ?? true)
  const [sorting, setSorting] = React.useState<SortingState>(initialState?.sorting ?? [])

  const table = useReactTable({
    data,
    columns,
    state: {
      expanded,
      sorting
    },
    onExpandedChange: setExpanded,
    onSortingChange: setSorting,
    getSubRows,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false
  })

  return (
    <NbUiTable mode={mode}>
      <NbUiTable.TableHead>
        {table.getHeaderGroups().map((headerGroup) => (
          <NbUiTable.TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              const { columnDef } = header.column
              const cells = columnDef?.meta?.cells ?? []
              return (
                <NbUiTable.TableHeader
                  key={header.id}
                  colspan={cells.length || header.colSpan}
                  onClick={header.column.getToggleSortingHandler()}>
                  {header.isPlaceholder ? null : (
                    <HeaderInnerContainer>
                      <div>{flexRender(header.column.columnDef.header, header.getContext())}</div>
                      {{
                        asc: <ArrowUpIcon fontSize={14} mode={mode} />,
                        desc: <ArrowDownIcon fontSize={14} mode={mode} />
                      }[header.column.getIsSorted() as string] ?? null}
                    </HeaderInnerContainer>
                  )}
                </NbUiTable.TableHeader>
              )
            })}
          </NbUiTable.TableRow>
        ))}
      </NbUiTable.TableHead>
      <NbUiTable.TableBody>
        {table.getRowModel().rows.map((row) => (
          <NbUiTable.TableRow
            key={row.id}
            onClick={onRowClick ? () => onRowClick(row.original) : undefined}>
            {row.getVisibleCells().map((cell) => {
              const cells = cell.column.columnDef.meta?.cells ?? []
              const cellsProps = cell.column.columnDef.meta?.cellsProps ?? []
              const cellProps = cell.column.columnDef.meta?.cellProps ?? {}
              if (cells?.length) {
                return cells.map((renderCell: any, renderCellIdx: number) => (
                  <NbUiTable.TableCell
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${cell.id}-${renderCellIdx}`}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...cellsProps[renderCellIdx]}>
                    {renderCell(cell.getContext())}
                  </NbUiTable.TableCell>
                ))
              }
              return (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <NbUiTable.TableCell key={cell.id} {...cellProps}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </NbUiTable.TableCell>
              )
            })}
          </NbUiTable.TableRow>
        ))}
      </NbUiTable.TableBody>
    </NbUiTable>
  )
}

export default ExpandedTable
